import * as React from 'react';
import {
    Box,
    Card,
    CardContent,
    MenuItem,
    FormControlLabel,
    useTheme,
    SxProps,
    Typography,
} from '@mui/material';
import { addNoteIcon } from '@europrocurement/l2d-icons';
import { ActivityProps, ActivityFilterProps } from '../../../types/ActivityHistoryTypes';
import { ActivityTimeline } from '../../molecules/ActivityTimeline';
import { CardHeader, Radio } from '../../atoms';
import { AdaptiveActionBar, AdaptiveActionBarProps } from './fragments/AdaptativeActionBar';
import { SkeletonLoader } from '../../skeletons';
import { EmptyBox } from '../../molecules';
import { ListActivityBone } from '../../skeletons/components/organisms/ListActivityBone/ListActivityBone';

export type ActivityHistoryCardProps = {
    /** Liste des activités à afficher */
    activities: ActivityProps[];
    /** Callback pour ajouter une note */
    onAddNote?: () => void;
    /** Callback pour éditer une note */
    onEditNote?: (
        noteId: string,
        content: string,
        highlighted: boolean,
        memoClient: boolean,
    ) => void;
    /** Callback pour supprimer une note */
    onDeleteNote?: (noteId: string) => void;
    /** Callback pour les changements de filtre */
    onFilterChange?: (filters: ActivityFilterProps[]) => void;
    /** Styles personnalisés */
    sx?: SxProps;
    /** Titre de la carte */
    title?: string;
    /** Chargement des activités */
    activitiesLoading?: boolean;
    /** Chargement des notes */
    notesLoading?: boolean;
    /** Affichage d'erreur dans la card */
    notesError?: string | null;
};

/**
 * Composant carte d'historique d'activités avec filtrage et actions
 */
export const ActivityHistoryCard: React.FunctionComponent<ActivityHistoryCardProps> = function ({
    activities,
    onAddNote,
    onEditNote,
    onDeleteNote,
    onFilterChange,
    sx,
    title = "Historique d'activités",
    activitiesLoading = false,
    notesLoading = false,
    notesError = null,
}) {
    const theme = useTheme();
    const [activeFilter, setActiveFilter] = React.useState('all');

    const filterOptions = React.useMemo(
        () => [
            { value: 'all', label: 'Toutes les activités' },
            { value: 'all_notes', label: 'Toutes les notes' },
            { value: 'important_notes_only', label: 'Uniquement les notes importantes' },
            { value: 'memo_client_notes_only', label: 'Uniquement les notes mémo client' },
            { value: 'all_actions', label: 'Toutes les actions' },
        ],
        [],
    );

    const filteredActivities = React.useMemo(
        () =>
            activities.filter((activity) => {
                if (
                    activeFilter === 'all' &&
                    (activity.type === 'note' || activity.type === 'action')
                ) {
                    return true;
                }
                if (activeFilter === 'all_notes' && activity.type === 'note') {
                    return true;
                }
                if (
                    activeFilter === 'important_notes_only' &&
                    activity.type === 'note' &&
                    activity.noteType === 'important'
                ) {
                    return true;
                }
                if (
                    activeFilter === 'memo_client_notes_only' &&
                    activity.type === 'note' &&
                    activity.memoClient
                ) {
                    return true;
                }
                if (activeFilter === 'all_actions' && activity.type === 'action') {
                    return true;
                }
                return false;
            }),
        [activities, activeFilter],
    );

    const countOfTotalActivities = activities.length;
    const countOfFilteredActivities = filteredActivities.length;

    React.useEffect(() => {
        if (onFilterChange) {
            onFilterChange([activeFilter as ActivityFilterProps]);
        }
    }, [activeFilter, onFilterChange]);

    const actions = React.useMemo(() => {
        if (!onAddNote) {
            return [];
        }

        return [
            {
                id: 'add-note',
                label: 'Ajouter une note',
                shortLabel: 'Ajouter',
                icon: addNoteIcon,
                onClick: onAddNote,
                variant: 'text' as const,
                color: theme.palette.grey[700],
            },
        ];
    }, [onAddNote, theme.palette.grey]);

    const dropdownAction = React.useMemo<AdaptiveActionBarProps['dropdownAction']>(
        () => ({
            label:
                filterOptions.find((opt) => opt.value === activeFilter)?.label ||
                'Toutes les activités',
            color: theme.palette.grey[700],
            variant: 'outlined' as const,
            size: 'small' as const,
            sx: {
                width: '100%',
            },
            style: {
                width: '40%',
            },
            children: filterOptions.map((option) => (
                <MenuItem key={option.value}>
                    <FormControlLabel
                        control={
                            <Radio
                                checked={activeFilter === option.value}
                                onChange={() => setActiveFilter(option.value)}
                            />
                        }
                        label={option.label}
                    />
                </MenuItem>
            )),
        }),
        [activeFilter, filterOptions, theme.palette.grey],
    );

    return (
        <Card
            sx={{
                paddingLeft: '0px',
                ...sx,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    paddingTop: '16px',
                    paddingBottom: '16px',
                }}
            >
                <CardHeader
                    title={title}
                    subtitle={
                        <Typography
                            variant="l2dsubtitles"
                            sx={{ marginLeft: '10px' }}
                        >
                            {`${countOfFilteredActivities}/${countOfTotalActivities}`}
                        </Typography>
                    }
                    headerSx={{ paddingTop: '0px' }}
                />
                <Box
                    sx={{
                        width: '500px',
                        flexShrink: 0,
                        marginRight: 2,
                        overflow: 'hidden',
                    }}
                >
                    <AdaptiveActionBar
                        actions={actions}
                        dropdownAction={dropdownAction}
                    />
                </Box>
            </Box>
            <CardContent sx={{ padding: 0 }}>
                <SkeletonLoader
                    isLoading={activitiesLoading || notesLoading}
                    skeleton={
                        <Box
                            sx={{ display: 'flex', justifyContent: 'center', padding: '20px 20px' }}
                        >
                            <ListActivityBone itemCount={3} />
                        </Box>
                    }
                    isInError={!!notesError}
                    errorChildren={
                        <Box
                            sx={{
                                padding: '20px',
                                textAlign: 'center',
                                color: 'error.main',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                            }}
                        >
                            <p>Erreur lors du chargement des notes</p>
                            <p style={{ fontSize: '0.9em', marginTop: '8px' }}>{notesError}</p>
                        </Box>
                    }
                >
                    <Box sx={{ padding: '0 20px 20px' }}>
                        {countOfFilteredActivities > 0 ? (
                            <ActivityTimeline
                                activities={filteredActivities}
                                onEditNote={onEditNote}
                                onDeleteNote={onDeleteNote}
                            />
                        ) : (
                            <EmptyBox
                                text="Pas d'activités à afficher"
                                sx={{
                                    marginBottom: '-40px',
                                }}
                            />
                        )}
                    </Box>
                </SkeletonLoader>
            </CardContent>
        </Card>
    );
};
